import {
  EMPTY_TOUR_OFFER_BOOKING_TERMS,
  EMPTY_TOUR_OFFER_CONTENT,
  TourOffer,
  TourOfferBookingTerms,
  TourOfferConfiguration,
  TourOfferConfigurationContent,
  TourOfferConfigurationDetailsInput,
  TourOfferContent,
  TourOfferHotelAddOns,
  TourOfferItinerary,
  TourOfferItineraryDayInput,
  TourOfferItineraryInput,
  TourOfferNewDepartures,
  TourOfferPaymentScheduleTemplate,
  TourOfferRelatedTour,
  TourOfferRelationshipsInput,
  TourOfferScheduleInput,
  TourOfferTravellersInput,
} from '~/types/tours/tourOffer';

import { cleanConfigurationContent, isConfigurationContentValid } from '../Bundles/ConfigurationEditor';

/** Every editable content field, with those added later filled in so the form always has them. */
export type NativeTourContent = Required<TourOfferContent>;

export interface NativeTourForm {
  name: string;
  status: TourOffer['status'];
  /** Brand code. */
  brand: string | null;
  content: NativeTourContent;
  bookingTerms: TourOfferBookingTerms;
  /** By configuration id. */
  configurations: Record<string, TourOfferConfigurationContent>;
  schedules: Array<TourOfferScheduleInput>;
  relationships: NativeTourRelationships;
  /** By configuration id. */
  optionDetails: Record<string, TourOfferConfigurationDetailsInput>;
  /** By configuration id. */
  optionHotels: Record<string, TourOfferHotelAddOns>;
  /** By configuration id; only a Salesforce tour's options have the full itinerary editor. */
  optionItineraries: Record<string, TourOfferItineraryInput>;
  /** The tour's gallery, the same on every option; the first is the hero. */
  gallery: Array<{ svcImageId: string; caption: string | null }>;
  travellers: TourOfferTravellersInput;
  optionalExperiences: Array<NativeTourOptionalExperience>;
  emailPreHeader: string | null;
  newDepartures: TourOfferNewDepartures;
}

export interface NativeTourOptionalExperience {
  id: string;
  name: string;
  description: string | null;
  photos: Array<string>;
  isTransfer: 'arrival' | 'departure' | null;
}

export const MAX_GALLERY_IMAGES = 30;

export interface NativeTourYearLink {
  tour: TourOfferRelatedTour;
  year: number | null;
}

export interface NativeTourRelationships {
  nextYear: NativeTourYearLink | null;
  previousYear: NativeTourYearLink | null;
  similar: Array<TourOfferRelatedTour>;
  sharedReviews: Array<TourOfferRelatedTour>;
}

export type NativeTourSection =
  | 'overview'
  | 'tour-options'
  | 'pricing'
  | 'dynamic-pricing'
  | 'booking'
  | 'optional-experiences'
  | 'media'
  | 'on-sale'
  | 'fine-print'
  | 'reviews'
  | 'operations';

export interface NativeTourSectionDefinition {
  key: NativeTourSection;
  label: string;
  path: string;
  description: string;
}

/** In the order a tour is set up. */
export const NATIVE_TOUR_SECTIONS: ReadonlyArray<NativeTourSectionDefinition> = [
  {
    key: 'overview',
    label: 'Overview',
    path: '',
    description: 'Name, brand, how the tour is described, related tours',
  },
  {
    key: 'tour-options',
    label: 'Tour options',
    path: 'tour-options',
    description: 'About, highlights, inclusions, itinerary',
  },
  {
    key: 'dynamic-pricing',
    label: 'Dynamic pricing',
    path: 'dynamic-pricing',
    description: 'Discounts, markups and LUX+ prices',
  },
  {
    key: 'booking',
    label: 'Booking & travellers',
    path: 'booking',
    description: 'Deposit, payment schedule, traveller requirements, transfers',
  },
  {
    key: 'optional-experiences',
    label: 'Optional experiences',
    path: 'optional-experiences',
    description: 'Experiences customers can add on tour',
  },
  { key: 'media', label: 'Media', path: 'media', description: 'Gallery, video, hosts and sponsors' },
  { key: 'on-sale', label: 'On sale', path: 'on-sale', description: 'Campaign schedules, tags and price display' },
  { key: 'fine-print', label: 'Fine print', path: 'fine-print', description: "What's included, what's not, the rules" },
  { key: 'reviews', label: 'User reviews', path: 'reviews', description: 'Shared reviews, ratings, moderation' },
  { key: 'operations', label: 'Operations', path: 'operations', description: 'DMC and LE contacts, from Salesforce' },
  {
    key: 'pricing',
    label: 'Cost price & inventory',
    path: 'pricing',
    description: 'Cost prices, departures, inventory',
  },
];

/**
 * Each supplier's service carries its own copy of a configuration with the same content, and
 * svc-tour saves an edit to every copy. The oldest service's copy is the one shown and edited.
 */
export function editableConfigurations(offer: TourOffer): Array<TourOfferConfiguration> {
  const seen = new Set<string>();
  return offer.services
    .flatMap((service) => service.configurations)
    .filter((configuration) => {
      if (seen.has(configuration.name)) {
        return false;
      }
      seen.add(configuration.name);
      return true;
    });
}

function contentFrom(content: TourOfferContent | null): NativeTourContent {
  const value = content ?? EMPTY_TOUR_OFFER_CONTENT;
  return {
    ...value,
    format: value.format ?? 'group',
    emailSubjectLine: value.emailSubjectLine ?? null,
    showValuedAtPrice: value.showValuedAtPrice ?? true,
  };
}

export function formFrom(offer: TourOffer): NativeTourForm {
  return {
    name: offer.name ?? '',
    status: offer.status,
    brand: offer.brand?.code ?? null,
    content: contentFrom(offer.content),
    bookingTerms: offer.bookingTerms ?? EMPTY_TOUR_OFFER_BOOKING_TERMS,
    configurations: Object.fromEntries(
      editableConfigurations(offer).map((configuration) => [
        configuration.id,
        {
          title: configuration.content?.title ?? null,
          description: configuration.content?.description ?? null,
          inclusions: configuration.content?.inclusions ?? [],
          itineraries: configuration.itineraries.map(({ id, title, text, accommodation, meals }) => ({
            id,
            title,
            text,
            accommodation,
            meals,
          })),
        },
      ]),
    ),
    schedules: offer.schedules.map(({ id, ...schedule }) => ({ id, ...schedule })),
    relationships: relationshipsFrom(offer),
    optionDetails: Object.fromEntries(
      editableConfigurations(offer).map((configuration) => [configuration.id, optionDetailsFrom(configuration)]),
    ),
    optionHotels: Object.fromEntries(
      editableConfigurations(offer).map((configuration) => [configuration.id, optionHotelsFrom(configuration)]),
    ),
    gallery: [...(editableConfigurations(offer)[0]?.images ?? [])]
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map(({ svcImageId, caption }) => ({ svcImageId, caption })),
    travellers: travellersFrom(offer),
    optionalExperiences: (offer.optionalExperiences ?? []).map(({ id, name, description, photos, isTransfer }) => ({
      id,
      name,
      description,
      photos,
      isTransfer,
    })),
    emailPreHeader: offer.emailPreHeader ?? null,
    newDepartures: offer.newDepartures ?? { added: false, year: null },
    optionItineraries: Object.fromEntries(
      editableConfigurations(offer).map((configuration) => [
        configuration.id,
        optionItineraryFrom(offer, configuration),
      ]),
    ),
  };
}

function travellersFrom(offer: TourOffer): TourOfferTravellersInput {
  const requirements = offer.travellerRequirements;
  const transfer = offer.transfers.find((candidate) => candidate.itineraryId === null);
  return {
    requirements: {
      idLevelRequired: requirements?.idLevelRequired === 'photo_id' ? 'photo_id' : 'passport',
      weightRequired: requirements?.weightRequired ?? false,
      flightDetailsRequired: requirements?.flightDetailsRequired ?? false,
      documentsRequired: requirements?.documentsRequired ?? [],
      departureAirports: requirements?.departureAirports ?? [],
    },
    transfer: transfer
      ? {
          arrivalAirport: transfer.arrivalAirport,
          arrivalTransferType: transfer.arrivalTransferType,
          departureAirport: transfer.departureAirport,
          departureTransferType: transfer.departureTransferType,
        }
      : null,
  };
}

export function itineraryDaysFrom(days: Array<TourOfferItinerary>): Array<TourOfferItineraryDayInput> {
  return days.map((day) => ({
    title: day.title,
    description: day.text.join('\n\n'),
    duration: day.duration,
    accommodation: day.accommodation,
    meals: day.meals,
    locations: day.locations ?? [],
    carouselImages: day.carouselImages ?? [],
    specialCallOuts: (day.specialCallOuts ?? []).map((callOut) => ({
      ...callOut,
      description: callOut.description ?? '',
    })),
  }));
}

function optionItineraryFrom(offer: TourOffer, configuration: TourOfferConfiguration): TourOfferItineraryInput {
  const days = configuration.itineraries;
  const transfer = offer.transfers.find((candidate) => candidate.itineraryId === days[0]?.id);
  return {
    days: itineraryDaysFrom(days),
    transfer: transfer
      ? {
          arrivalAirport: transfer.arrivalAirport,
          arrivalTransferType: transfer.arrivalTransferType,
          departureAirport: transfer.departureAirport,
          departureTransferType: transfer.departureTransferType,
        }
      : null,
  };
}

export const ITINERARY_MEALS = ['Breakfast', 'Lunch', 'Dinner'];

/** What's wrong with an itinerary: by day, and overall against the option's length. */
export function itineraryProblems(
  itinerary: TourOfferItineraryInput,
  lengthInDays: number | null,
): { total: string | null; days: Array<string | null> } {
  const totalDays = itinerary.days.reduce(
    (total, day) => total + (Number.isFinite(day.duration) ? day.duration : 0),
    0,
  );
  let total: string | null = null;
  if (itinerary.days.length === 0) {
    total = 'Add at least one day';
  } else if (lengthInDays !== null && totalDays !== lengthInDays) {
    total = `The days add up to ${totalDays}, but the tour runs for ${lengthInDays}`;
  }
  return {
    total,
    days: itinerary.days.map((day) => {
      if (!day.title.trim() || !day.description.trim()) {
        return 'Needs a title and a description';
      }
      if (!Number.isInteger(day.duration) || day.duration < 1) {
        return 'Lasts at least one day';
      }
      if (day.specialCallOuts.some((callOut) => !callOut.description.trim())) {
        return 'Every special call-out needs a description';
      }
      if (day.carouselImages.some((image) => !image.svcImageId)) {
        return 'Every image needs uploading';
      }
      return null;
    }),
  };
}

export function isItineraryInvalid(itinerary: TourOfferItineraryInput, lengthInDays: number | null): boolean {
  const problems = itineraryProblems(itinerary, lengthInDays);
  return !!problems.total || problems.days.some(Boolean);
}

function optionHotelsFrom(configuration: TourOfferConfiguration): TourOfferHotelAddOns {
  const stored = configuration.content?.recommendedHotelAddOns as Partial<TourOfferHotelAddOns> | null | undefined;
  const startBedbank = stored?.startBedbank ?? [];
  const endBedbank = stored?.endBedbank ?? [];
  return {
    startLE: stored?.startLE ?? [],
    endLE: stored?.endLE ?? [],
    startBedbank,
    endBedbank,
    startBedbankRoom: startBedbank.map((_, index) => stored?.startBedbankRoom?.[index] ?? null),
    endBedbankRoom: endBedbank.map((_, index) => stored?.endBedbankRoom?.[index] ?? null),
  };
}

function optionDetailsFrom(configuration: TourOfferConfiguration): TourOfferConfigurationDetailsInput {
  const { content } = configuration;
  const summary = configuration.itinerarySummary;
  return {
    minTravellerAge: content?.minTravellerAge ?? null,
    maxTravellerAge: content?.maxTravellerAge ?? null,
    minPax: content?.minPax ?? null,
    maxPax: content?.maxPax ?? null,
    isReverseItinerary: content?.isReverseItinerary ?? false,
    privateRequest: {
      enabled: content?.privateRequest?.enabled ?? false,
      fromPrice: content?.privateRequest?.fromPrice ?? null,
    },
    route: {
      startLocationName: summary?.startLocationName ?? null,
      startCountryName: summary?.startCountryName ?? null,
      endLocationName: summary?.endLocationName ?? null,
      endCountryName: summary?.endCountryName ?? null,
      routeMapImage: summary?.routeMapImage ?? null,
    },
    packages: configuration.packages.map(({ id, inclusions }) => ({ id, inclusions })),
  };
}

/** Duplicate titles, ignoring case and spaces, which customers would see twice. */
export function duplicateTitles(titles: Array<string>): Array<string> {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const title of titles) {
    const key = title.trim().toLowerCase();
    if (key && seen.has(key)) {
      duplicates.add(title.trim());
    }
    seen.add(key);
  }
  return [...duplicates];
}

/** What's wrong with an option's details, by field. */
/** The tour form's rule: at least 2 travellers, or 1 on the few tours that allow a single traveller. */
export function leastPaxFor(legacyTourId: string | null, singlePaxTourIds: Array<string> | undefined): number {
  return !singlePaxTourIds || (legacyTourId && singlePaxTourIds.includes(legacyTourId)) ? 1 : 2;
}

export function optionDetailsProblems(
  details: TourOfferConfigurationDetailsInput,
  leastPax = 1,
): Partial<Record<'ages' | 'pax' | 'privateRequest' | 'packages', string>> {
  const problems: Partial<Record<'ages' | 'pax' | 'privateRequest' | 'packages', string>> = {};
  const { minTravellerAge, maxTravellerAge, minPax, maxPax } = details;
  if (minTravellerAge !== null && maxTravellerAge !== null && minTravellerAge > maxTravellerAge) {
    problems.ages = 'The minimum age is above the maximum';
  }
  if ((minPax !== null && minPax < leastPax) || (maxPax !== null && maxPax < leastPax)) {
    problems.pax = `At least ${leastPax}`;
  } else if (minPax !== null && maxPax !== null && minPax > maxPax) {
    problems.pax = 'The minimum is above the maximum';
  }
  if (details.privateRequest.enabled && details.privateRequest.fromPrice === null) {
    problems.privateRequest = 'Set the from price, or turn private requests off';
  }
  const packageProblem = details.packages.find(
    ({ inclusions }) =>
      inclusions.some((inclusion) => !inclusion.title.trim()) ||
      duplicateTitles(inclusions.map((inclusion) => inclusion.title)).length > 0,
  );
  if (packageProblem) {
    problems.packages = 'Every package inclusion needs a title, and titles must not repeat';
  }
  return problems;
}

function relationshipsFrom(offer: TourOffer): NativeTourRelationships {
  const links = offer.relationships;
  const yearLink = (link: (TourOfferRelatedTour & { year: number | null }) | null | undefined) =>
    link
      ? { tour: { tourOfferId: link.tourOfferId, legacyTourId: link.legacyTourId, name: link.name }, year: link.year }
      : null;
  return {
    nextYear: yearLink(links?.nextYear),
    previousYear: yearLink(links?.previousYear),
    similar: links?.similar ?? [],
    sharedReviews: links?.sharedReviews ?? [],
  };
}

export function relationshipsInput(relationships: NativeTourRelationships): TourOfferRelationshipsInput {
  const yearLink = (link: NativeTourYearLink | null) =>
    link && link.year !== null ? { tourOfferId: link.tour.tourOfferId, year: link.year } : null;
  return {
    nextYear: yearLink(relationships.nextYear),
    previousYear: yearLink(relationships.previousYear),
    similar: relationships.similar.map((tour) => tour.tourOfferId),
    sharedReviews: relationships.sharedReviews.map((tour) => tour.tourOfferId),
  };
}

/** A next or previous year tour is for this year up to three years ahead, as on the tour form. */
export const RELATED_YEARS = Array.from({ length: 4 }, (_, index) => new Date().getFullYear() + index);

/** The checks the campaign schedule form makes, by field, so each can show where it's wrong. */
export function scheduleProblems(
  schedule: TourOfferScheduleInput,
  earlyAccessBrands: Array<string>,
): Partial<Record<keyof TourOfferScheduleInput, string>> {
  const at = (value: string | null) => (value ? new Date(value).getTime() : Number.NaN);
  const problems: Partial<Record<keyof TourOfferScheduleInput, string>> = {};
  for (const field of ['offlineStartDate', 'startDate', 'endDate', 'offlineEndDate'] as const) {
    if (!schedule[field]) {
      problems[field] = 'Required';
    }
  }
  if (at(schedule.startDate) > at(schedule.endDate)) {
    problems.endDate = 'Must be after the start date';
  }
  if (at(schedule.offlineStartDate) > at(schedule.startDate)) {
    problems.offlineStartDate = 'Must be on or before the start date';
  }
  if (at(schedule.offlineEndDate) < at(schedule.endDate)) {
    problems.offlineEndDate = 'Must be on or after the end date';
  }
  if (schedule.earlyAccessStartDate) {
    if (at(schedule.earlyAccessStartDate) < at(schedule.offlineStartDate)) {
      problems.earlyAccessStartDate = 'Must be on or after the offline start date';
    } else if (at(schedule.earlyAccessStartDate) > at(schedule.startDate)) {
      problems.earlyAccessStartDate = 'Must be on or before the start date';
    } else if (schedule.whiteLabelBrands.some((brand) => !earlyAccessBrands.includes(brand))) {
      problems.earlyAccessStartDate = `Early access is only for ${earlyAccessBrands.join(', ')}`;
    }
  }
  if (schedule.regions.length === 0) {
    problems.regions = 'Pick at least one region';
  }
  if (schedule.whiteLabelBrands.length === 0) {
    problems.whiteLabelBrands = 'Pick at least one brand';
  }
  return problems;
}

/** Why the chosen payment schedule templates can't be saved, as svc-tour would reject them. */
export function templateProblem(
  ids: Array<string>,
  templates: Array<TourOfferPaymentScheduleTemplate> | undefined,
): string | null {
  if (ids.length <= 1 || !templates) {
    return null;
  }
  const chosen = ids.map((id) => templates.find((template) => template.id === id));
  if (chosen.some((template) => !template?.leadTimeRange)) {
    return 'A template without a lead time range must be the only one';
  }
  const ranges = chosen.map((template) => template.leadTimeRange).sort((a, b) => a.minDays - b.minDays);
  const overlaps = ranges.some(
    (range, index) => index > 0 && (ranges[index - 1].maxDays === null || ranges[index - 1].maxDays >= range.minDays),
  );
  return overlaps ? 'Lead time ranges must not overlap' : null;
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

const isNegative = (value: number | null) => value !== null && (Number.isNaN(value) || value < 0);

export const VIMEO_ID_PATTERN = /^\d+$/;

export interface NativeTourChanges {
  name: boolean;
  status: boolean;
  brand: boolean;
  content: boolean;
  bookingTerms: boolean;
  configurationIds: Array<string>;
  optionDetailIds: Array<string>;
  optionHotelIds: Array<string>;
  optionItineraryIds: Array<string>;
  gallery: boolean;
  travellers: boolean;
  optionalExperiences: boolean;
  marketing: boolean;
  schedules: boolean;
  relationships: boolean;
}

export const NEW_DEPARTURES_YEARS = Array.from({ length: 11 }, (_, index) => new Date().getFullYear() + index);

export function newDeparturesProblem(newDepartures: TourOfferNewDepartures): string | null {
  const { added, year } = newDepartures;
  if (added && year !== null && !NEW_DEPARTURES_YEARS.includes(year)) {
    return `Pick a year from ${NEW_DEPARTURES_YEARS[0]} to ${NEW_DEPARTURES_YEARS[NEW_DEPARTURES_YEARS.length - 1]}`;
  }
  return null;
}

export function changesBetween(form: NativeTourForm, saved: NativeTourForm): NativeTourChanges {
  return {
    name: form.name.trim() !== saved.name,
    status: form.status !== saved.status,
    brand: form.brand !== saved.brand,
    content: !same(form.content, saved.content),
    bookingTerms: !same(form.bookingTerms, saved.bookingTerms),
    configurationIds: Object.keys(saved.configurations).filter(
      (id) => !same(cleanConfigurationContent(form.configurations[id]), saved.configurations[id]),
    ),
    optionDetailIds: Object.keys(saved.optionDetails).filter(
      (id) => !same(form.optionDetails[id], saved.optionDetails[id]),
    ),
    optionHotelIds: Object.keys(saved.optionHotels).filter(
      (id) => !same(form.optionHotels[id], saved.optionHotels[id]),
    ),
    optionItineraryIds: Object.keys(saved.optionItineraries).filter(
      (id) => !same(form.optionItineraries[id], saved.optionItineraries[id]),
    ),
    gallery: !same(form.gallery, saved.gallery),
    travellers: !same(form.travellers, saved.travellers),
    optionalExperiences: !same(form.optionalExperiences, saved.optionalExperiences),
    marketing: form.emailPreHeader !== saved.emailPreHeader || !same(form.newDepartures, saved.newDepartures),
    schedules: !same(form.schedules, saved.schedules),
    relationships: !same(relationshipsInput(form.relationships), relationshipsInput(saved.relationships)),
  };
}

export interface NativeTourSectionState {
  isChanged: boolean;
  isInvalid: boolean;
}

// The content record is shared by several sections; each owns some of its fields.
const CONTENT_FIELDS_BY_SECTION: Partial<Record<NativeTourSection, Array<keyof NativeTourContent>>> = {
  overview: ['tourStyle', 'format', 'guideType', 'activityLevel', 'categories', 'operatedBy'],
  media: ['videoId', 'hosts', 'hostsDefaultImage', 'sponsors'],
  'on-sale': ['campaignTags', 'emailSubjectLine', 'showPricePerDay', 'showValuedAtPrice', 'highlightPackages'],
  'fine-print': ['finePrint'],
};

function contentChanged(form: NativeTourForm, saved: NativeTourForm, section: NativeTourSection): boolean {
  return (CONTENT_FIELDS_BY_SECTION[section] ?? []).some((field) => !same(form.content[field], saved.content[field]));
}

/** The bundle builder's checks, plus inclusion titles that don't repeat. */
export function isOptionContentValid(content: TourOfferConfigurationContent): boolean {
  return (
    isConfigurationContentValid(content) && duplicateTitles(content.inclusions.map((entry) => entry.title)).length === 0
  );
}

export interface NativeTourLookups {
  earlyAccessBrands: Array<string>;
  leastPax: number;
  /** By configuration id. */
  lengthsInDays: Record<string, number | null>;
  templates: Array<TourOfferPaymentScheduleTemplate> | undefined;
}

export function sectionStates(
  form: NativeTourForm,
  saved: NativeTourForm,
  changes: NativeTourChanges,
  lookups: NativeTourLookups,
): Record<NativeTourSection, NativeTourSectionState> {
  const { content, bookingTerms, relationships } = form;
  const isYearLinkInvalid = (link: NativeTourYearLink | null) => !!link && link.year === null;
  return {
    overview: {
      isChanged:
        changes.name ||
        changes.status ||
        changes.brand ||
        contentChanged(form, saved, 'overview') ||
        !same(
          { ...relationshipsInput(form.relationships), sharedReviews: [] },
          { ...relationshipsInput(saved.relationships), sharedReviews: [] },
        ),
      isInvalid:
        !form.name.trim() || isYearLinkInvalid(relationships.nextYear) || isYearLinkInvalid(relationships.previousYear),
    },
    'tour-options': {
      isChanged:
        changes.configurationIds.length > 0 ||
        changes.optionDetailIds.length > 0 ||
        changes.optionHotelIds.length > 0 ||
        changes.optionItineraryIds.length > 0,
      // Only edited options are saved, so only they need to be valid.
      isInvalid:
        changes.configurationIds.some((id) => !isOptionContentValid(form.configurations[id])) ||
        changes.optionDetailIds.some(
          (id) => Object.keys(optionDetailsProblems(form.optionDetails[id], lookups.leastPax)).length > 0,
        ) ||
        changes.optionItineraryIds.some((id) =>
          isItineraryInvalid(form.optionItineraries[id], lookups.lengthsInDays[id] ?? null),
        ),
    },
    // Saved on its own, from inside the section.
    pricing: { isChanged: false, isInvalid: false },
    booking: {
      isChanged: changes.bookingTerms || changes.travellers,
      isInvalid:
        !!depositAmountProblem(bookingTerms) ||
        [bookingTerms.depositDueDays, bookingTerms.finalPaymentDueDays].some(isNegative) ||
        !!templateProblem(bookingTerms.scheduleTemplateIds, lookups.templates),
    },
    'optional-experiences': {
      isChanged: changes.optionalExperiences,
      isInvalid: form.optionalExperiences.some((extra) => !extra.name.trim() || extra.photos.some((photo) => !photo)),
    },
    // Saved on its own, not with the page.
    'dynamic-pricing': { isChanged: false, isInvalid: false },
    reviews: {
      isChanged: !same(
        relationshipsInput(form.relationships).sharedReviews,
        relationshipsInput(saved.relationships).sharedReviews,
      ),
      isInvalid: false,
    },
    media: {
      isChanged: changes.gallery || contentChanged(form, saved, 'media'),
      isInvalid:
        form.gallery.length > MAX_GALLERY_IMAGES ||
        form.gallery.some((image) => !image.svcImageId) ||
        (!!content.videoId && !VIMEO_ID_PATTERN.test(content.videoId)) ||
        content.hosts.some((host) => !host.name.trim() || !host.svcImageId) ||
        content.sponsors.some((sponsor) => !sponsor.name.trim() || !sponsor.svcImageId),
    },
    'on-sale': {
      isChanged: changes.schedules || changes.marketing || contentChanged(form, saved, 'on-sale'),
      isInvalid:
        form.schedules.some(
          (schedule) => Object.keys(scheduleProblems(schedule, lookups.earlyAccessBrands)).length > 0,
        ) || !!newDeparturesProblem(form.newDepartures),
    },
    'fine-print': {
      isChanged: contentChanged(form, saved, 'fine-print'),
      isInvalid: content.finePrint.some((item) => !item.title.trim() || !item.body.trim()),
    },
    operations: { isChanged: false, isInvalid: false },
  };
}

/** A percentage deposit is a share of the price, above 0 and below 1; a flat one is an amount. */
export function depositAmountProblem(terms: TourOfferBookingTerms): string | null {
  const amount = terms.depositAmount;
  if (terms.depositType === 'percentage') {
    return amount === null || Number.isNaN(amount) || amount <= 0 || amount >= 1 ? 'Between 1% and 99%' : null;
  }
  if (terms.depositType === 'flat') {
    return amount === null || Number.isNaN(amount) || amount <= 0 ? 'Must be more than 0' : null;
  }
  return null;
}
